/*********************************************************************************************
 * Number odometer — rolls digits vertically when a value changes.
 * Unchanged digits stay still; non-digit characters (spaces, commas, €) are static.
 *
 * initNumberOdometer() scans [data-odometer-group] elements and returns updateOdometer(),
 * which can also be used directly on any element without prior init.
 */
(function (window) {
	const DEFAULTS = {
		duration: 0.9,
		ease: 'power3.out',
		digitCycles: 2,
		digitStagger: 0.04,
	};

	function prefersReducedMotion() {
		return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	function isDigit(char) {
		return char >= '0' && char <= '9';
	}

	/*****************************************************************************************
	 * Build the digit sequence (start -> ... -> end) rolling forward through digitCycles
	 * full loops before landing on the target digit.
	 */
	function buildDigitSequence(startDigit, endDigit, digitCycles) {
		const sequence = [startDigit];
		let current = startDigit;
		const steps = digitCycles * 10 + (((endDigit - startDigit) % 10) + 10) % 10;
		for (let i = 0; i < steps; i++) {
			current = (current + 1) % 10;
			sequence.push(current);
		}
		return sequence;
	}

	function renderStaticText(el, text) {
		el.textContent = text;
		el.dataset.odometerValue = text;
	}

	/*****************************************************************************************
	 * Animate el's displayed text from its previous value to `text`.
	 *
	 * @param {HTMLElement} el
	 * @param {string} text - the new formatted value (e.g. "24,70 €")
	 * @param {Object} [options]
	 * @returns {Promise<void>}
	 */
	function updateOdometer(el, text, options) {
		if (!el) return Promise.resolve();
		const opts = Object.assign({}, DEFAULTS, options);
		const previousText = el.dataset.odometerValue;

		// Coalesce rapid successive calls: finish/queue rather than overlap animations.
		if (el._mmOdometerBusy) {
			el._mmOdometerPending = { text, options };
			return Promise.resolve();
		}

		if (previousText === undefined || previousText === text || !window.gsap || prefersReducedMotion()) {
			renderStaticText(el, text);
			return Promise.resolve();
		}

		el._mmOdometerBusy = true;

		const oldChars = previousText.split('');
		const newChars = text.split('');
		const length = Math.max(oldChars.length, newChars.length);

		el.innerHTML = '';
		const columns = [];

		// Right-to-left stagger: the rightmost digit starts first.
		let digitIndexFromRight = 0;
		const charNodes = [];
		for (let i = 0; i < length; i++) {
			charNodes.push({ oldChar: oldChars[i], newChar: newChars[i] });
		}

		charNodes.forEach((pair) => {
			if (pair.newChar === undefined) return;
			if (isDigit(pair.newChar) && isDigit(pair.oldChar) && pair.oldChar !== pair.newChar) {
				digitIndexFromRight++;
			}
		});

		// Keep unchanged characters (static digits, spaces, commas, €…) as plain contiguous
		// text instead of one <span> per character: isolating every glyph breaks the
		// browser's normal kerning/shaping and reads as a change in the base font style.
		// Only the digit(s) that actually roll need to be isolated into their own column.
		let staticBuffer = '';
		function flushStaticBuffer() {
			if (staticBuffer) {
				el.appendChild(document.createTextNode(staticBuffer));
				staticBuffer = '';
			}
		}

		charNodes.forEach((pair) => {
			if (pair.newChar === undefined) return;

			if (isDigit(pair.newChar) && isDigit(pair.oldChar) && pair.oldChar !== pair.newChar) {
				flushStaticBuffer();

				const startDigit = parseInt(pair.oldChar, 10);
				const endDigit = parseInt(pair.newChar, 10);
				const sequence = buildDigitSequence(startDigit, endDigit, opts.digitCycles);

				const wrapper = document.createElement('span');
				wrapper.className = 'mm-odometer-digit';
				const strip = document.createElement('span');
				strip.className = 'mm-odometer-digit-strip';
				sequence.forEach((digit) => {
					const digitSpan = document.createElement('span');
					digitSpan.textContent = digit;
					strip.appendChild(digitSpan);
				});
				wrapper.appendChild(strip);
				el.appendChild(wrapper);

				const staggerIndex = digitIndexFromRight - 1;
				digitIndexFromRight--;

				columns.push({
					wrapper,
					strip,
					sequenceLength: sequence.length,
					delay: Math.max(0, staggerIndex) * opts.digitStagger,
				});
			} else {
				staticBuffer += pair.newChar;
			}
		});
		flushStaticBuffer();

		if (!columns.length) {
			renderStaticText(el, text);
			el._mmOdometerBusy = false;
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			let remaining = columns.length;
			columns.forEach((col) => {
				const digitHeight = col.strip.children[0] ? col.strip.children[0].getBoundingClientRect().height : 0;
				// Clip the wrapper to exactly one row so it matches the surrounding text's
				// own line-height instead of an arbitrary fixed value.
				col.wrapper.style.height = digitHeight + 'px';
				const targetY = -(col.sequenceLength - 1) * digitHeight;
				window.gsap.fromTo(
					col.strip,
					{ y: 0 },
					{
						y: targetY,
						duration: opts.duration,
						ease: opts.ease,
						delay: col.delay,
						onComplete() {
							remaining--;
							if (remaining === 0) finish();
						},
					}
				);
			});

			function finish() {
				renderStaticText(el, text);
				el._mmOdometerBusy = false;
				resolve();

				if (el._mmOdometerPending) {
					const pending = el._mmOdometerPending;
					el._mmOdometerPending = null;
					updateOdometer(el, pending.text, pending.options);
				}
			}
		});
	}

	function initNumberOdometer() {
		const groups = document.querySelectorAll('[data-odometer-group]');
		groups.forEach((el) => {
			if (el.dataset.odometerValue === undefined) {
				el.dataset.odometerValue = el.textContent;
			}
		});
		return updateOdometer;
	}

	window.initNumberOdometer = initNumberOdometer;
	window.updateOdometer = updateOdometer;
})(window);
